import { supabase } from '../lib/supabase';
import type { User } from '@supabase/supabase-js';
import { logger } from '../utils/logger';

export interface AuthError {
  message: string;
  code?: string;
}

/**
 * Sign up with email and password
 */
export async function signUpWithEmail(
  email: string,
  password: string
): Promise<{ user: User | null; error: AuthError | null }> {
  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    if (error) {
      return {
        user: null,
        error: {
          message: error.message,
          code: error.status?.toString(),
        },
      };
    }

    return {
      user: data.user,
      error: null,
    };
  } catch (error: any) {
    return {
      user: null,
      error: {
        message: error.message || '注册失败，请重试',
      },
    };
  }
}

/**
 * Sign in with email and password
 */
export async function signInWithEmail(
  email: string,
  password: string
): Promise<{ user: User | null; error: AuthError | null }> {
  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      return {
        user: null,
        error: {
          message: error.message,
          code: error.status?.toString(),
        },
      };
    }

    return {
      user: data.user,
      error: null,
    };
  } catch (error: any) {
    return {
      user: null,
      error: {
        message: error.message || '登录失败，请重试',
      },
    };
  }
}

/**
 * Sign in with Google OAuth
 * 
 * Based on Supabase documentation: https://supabase.com/docs/guides/auth/social-login/auth-google
 * 
 * This function initiates the OAuth flow. The user will be redirected to Google's consent screen,
 * and then redirected back to the application. The session will be automatically detected
 * by Supabase client (detectSessionInUrl: true).
 * 
 * For implicit flow (default), the user is redirected and session is automatically established.
 * The redirectTo URL should be added to your Supabase project's redirect allow list.
 */
export async function signInWithGoogle(): Promise<{ error: AuthError | null }> {
  try {
    // Get the current origin for redirect
    const redirectTo = `${window.location.origin}${window.location.pathname}`;
    
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo,
        // Request offline access to get refresh token
        // This allows the app to access Google services on user's behalf
        queryParams: {
          access_type: 'offline',
          prompt: 'consent',
        },
      },
    });

    if (error) {
      if (import.meta.env.DEV) {
        console.error('Google OAuth error:', error);
      }
      return {
        error: {
          message: error.message,
          code: error.status?.toString(),
        },
      };
    }

    // If successful, the user will be redirected to Google
    // The session will be automatically established when they return
    // No need to return data here as the redirect happens automatically
    
    return { error: null };
  } catch (error: any) {
    if (import.meta.env.DEV) {
      console.error('Google OAuth exception:', error);
    }
    return {
      error: {
        message: error.message || 'Google登录失败，请重试',
      },
    };
  }
}

/**
 * Sign out
 */
export async function signOut(): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.signOut();

    if (error) {
      return {
        error: {
          message: error.message,
          code: error.status?.toString(),
        },
      };
    }

    return { error: null };
  } catch (error: any) {
    return {
      error: {
        message: error.message || '登出失败，请重试',
      },
    };
  }
}

/**
 * Get current user
 */
export async function getCurrentUser(): Promise<User | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    return user;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error getting current user:', error);
    }
    return null;
  }
}

/**
 * Get current session
 */
export async function getCurrentSession() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session;
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error getting current session:', error);
    }
    return null;
  }
}

/**
 * Listen to auth state changes
 */
export function onAuthStateChange(
  callback: (user: User | null) => void
): { unsubscribe: () => void } {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(
    async (event, session) => {
      callback(session?.user ?? null);
    }
  );

  return {
    unsubscribe: () => {
      subscription.unsubscribe();
    },
  };
}

/**
 * Migrate anonymous user queries to authenticated user
 * 
 * This function transfers all queries created by an anonymous user (identified by user_id)
 * to the authenticated user account by updating auth_user_id to the UID from auth.users.
 * The user_id field is kept unchanged (remains as anonymous ID) for continuity.
 * 
 * @param anonymousUserId - The anonymous user ID (from localStorage)
 * @param authUserId - The authenticated user UID (from Supabase Auth, auth.users.id)
 * @returns Promise with success status and error information
 */
export async function migrateAnonymousQueries(
  anonymousUserId: string,
  authUserId: string
): Promise<{ success: boolean; error: AuthError | null; migratedCount?: number }> {
  try {
    if (import.meta.env.DEV) {
      console.log('🔄 Starting query migration:', {
        anonymousUserId,
        authUserId,
        timestamp: new Date().toISOString()
      });
    }

    // First, check how many queries need to be migrated
    // Query anonymous queries (user_id = anonymousUserId and auth_user_id IS NULL)
    const { count: queryCount, error: countError } = await supabase
      .from('query_history')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', anonymousUserId)
      .is('auth_user_id', null);

    if (countError) {
      if (import.meta.env.DEV) {
        console.error('❌ Error counting queries to migrate:', {
          error: countError,
          code: countError.code,
          message: countError.message
        });
      }
      return {
        success: false,
        error: {
          message: `无法统计待迁移的查询: ${countError.message}`,
          code: countError.code,
        },
        migratedCount: 0,
      };
    }

    const queriesToMigrate = queryCount || 0;
    if (import.meta.env.DEV) {
      console.log(`📊 Found ${queriesToMigrate} queries to migrate`);
    }

    if (queriesToMigrate === 0) {
      if (import.meta.env.DEV) {
        console.log('✅ No queries to migrate');
      }
      return {
        success: true,
        error: null,
        migratedCount: 0,
      };
    }

    // Update all queries from anonymous user to authenticated user
    // Update auth_user_id to UID (keep user_id unchanged as anonymous ID)
    const { data, error } = await supabase
      .from('query_history')
      .update({ 
        auth_user_id: authUserId // Set auth_user_id to UID from auth.users
        // user_id remains unchanged (anonymous ID)
      })
      .eq('user_id', anonymousUserId)
      .is('auth_user_id', null)
      .select('id'); // Select to get count of updated rows

    if (error) {
      if (import.meta.env.DEV) {
        console.error('❌ Error migrating anonymous queries:', {
          error,
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
          anonymousUserId,
          authUserId,
          queriesToMigrate
        });
      }

      // Check if error is due to RLS policy
      if (error.code === '42501') {
        return {
          success: false,
          error: {
            message: '迁移失败：权限不足。请确保已运行数据库迁移以添加迁移策略。',
            code: error.code,
          },
          migratedCount: 0,
        };
      }

      return {
        success: false,
        error: {
          message: error.message || '迁移查询数据失败',
          code: error.code,
        },
        migratedCount: 0,
      };
    }

    const migratedCount = data?.length || 0;
    
    // Verify migration was successful
    if (migratedCount !== queriesToMigrate) {
      if (import.meta.env.DEV) {
        console.warn('⚠️ Migration count mismatch:', {
          expected: queriesToMigrate,
          actual: migratedCount
        });
      }
    }

    if (import.meta.env.DEV) {
      console.log('✅ Successfully migrated queries:', {
        migratedCount,
        expectedCount: queriesToMigrate,
        anonymousUserId,
        authUserId,
        note: 'user_id kept unchanged, auth_user_id set to authenticated user UID'
      });
    }

    return {
      success: true,
      error: null,
      migratedCount,
    };
  } catch (error: any) {
    if (import.meta.env.DEV) {
      console.error('❌ Exception during query migration:', {
        error: error.message,
        stack: error.stack,
        anonymousUserId,
        authUserId
      });
    }
    return {
      success: false,
      error: {
        message: error.message || '迁移查询数据失败',
        code: error.code,
      },
      migratedCount: 0,
    };
  }
}

/**
 * Get user email (from user metadata or email)
 */
export function getUserEmail(user: User | null): string | null {
  if (!user) return null;
  
  // Try to get email from user metadata (for OAuth providers)
  if (user.user_metadata?.email) {
    return user.user_metadata.email;
  }
  
  // Fall back to user.email
  return user.email || null;
}

/**
 * Get primary provider from identities array
 * Returns the primary provider, prioritizing SSO providers over email
 * Version: 1.2.13
 */
function getPrimaryProvider(identities: Array<{ provider?: string }>): string | null {
  if (!identities || identities.length === 0) {
    return null;
  }

  // 1.2.13: 如果有多个 identities，优先选择非 'email' 的 provider（SSO优先）
  for (const identity of identities) {
    if (identity.provider && identity.provider !== 'email') {
      if (import.meta.env.DEV) {
        logger.log('🔍 getPrimaryProvider: Found SSO provider:', identity.provider);
      }
      return identity.provider;
    }
  }

  // 1.2.13: 如果所有 identities 都是 'email' 或只有一个 identity，返回第一个
  const firstProvider = identities[0]?.provider;
  if (firstProvider) {
    if (import.meta.env.DEV) {
      logger.log('🔍 getPrimaryProvider: Found provider (email or single):', firstProvider);
    }
    return firstProvider;
  }

  return null;
}

/**
 * Get user login provider
 * Returns the provider name (e.g., 'email', 'google', 'github', etc.)
 * Version: 1.2.13 - Use Supabase Authentication Users identities as primary source
 */
export function getUserProvider(user: User | null): string | null {
  if (!user) return null;
  
  // 1.2.13: 优先使用 identities 数组，这是 Supabase Authentication 的权威数据源
  
  // Priority 1: Check identities array (most reliable source from Supabase Auth)
  if (user.identities && user.identities.length > 0) {
    const provider = getPrimaryProvider(user.identities);
    if (provider) {
      if (import.meta.env.DEV) {
        logger.log('🔍 getUserProvider: Found provider from identities:', provider, {
          allIdentities: user.identities.map(i => i.provider),
          identitiesCount: user.identities.length
        });
      }
      return provider;
    }
  }

  // Priority 2: Check app_metadata.provider (fallback for edge cases)
  if (user.app_metadata?.provider) {
    if (import.meta.env.DEV) {
      logger.log('🔍 getUserProvider: Found provider from app_metadata (fallback):', user.app_metadata.provider);
    }
    return user.app_metadata.provider;
  }
  
  // Priority 3: Default to email if no provider found
  if (import.meta.env.DEV) {
    logger.log('🔍 getUserProvider: No provider found, defaulting to email', {
      hasIdentities: !!user.identities,
      identitiesCount: user.identities?.length || 0,
      hasAppMetadata: !!user.app_metadata
    });
  }
  return 'email';
}

/**
 * Check if user logged in with email/password
 * Version: 1.2.13 - Uses improved getUserProvider function
 */
export function isEmailPasswordUser(user: User | null): boolean {
  const provider = getUserProvider(user);
  return provider === 'email';
}

/**
 * Get provider display name
 * Version: 1.2.12 - Added support for generic OAuth provider
 */
export function getProviderDisplayName(provider: string | null): string {
  if (!provider) return 'Unknown';
  
  const providerMap: Record<string, string> = {
    'email': 'Email',
    'google': 'Google',
    'github': 'GitHub',
    'facebook': 'Facebook',
    'twitter': 'Twitter',
    'apple': 'Apple',
    'azure': 'Azure AD',
    'linkedin': 'LinkedIn',
    'oauth': 'OAuth', // 1.2.12: Generic OAuth provider display name (universal term)
  };
  
  return providerMap[provider.toLowerCase()] || provider;
}

/**
 * Get user display name
 */
export function getUserDisplayName(user: User | null): string {
  if (!user) return '未登录';
  
  // Try to get name from user metadata
  if (user.user_metadata?.full_name) {
    return user.user_metadata.full_name;
  }
  
  if (user.user_metadata?.name) {
    return user.user_metadata.name;
  }
  
  // Fall back to email
  return getUserEmail(user) || '用户';
}

/**
 * Update user password
 * Requires old password verification
 * Version: 1.2.8
 */
export async function updatePassword(
  oldPassword: string,
  newPassword: string
): Promise<{ error: AuthError | null }> {
  try {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !user.email) {
      return {
        error: {
          message: '用户未登录',
        },
      };
    }

    // Verify old password by attempting to sign in
    const { error: verifyError } = await supabase.auth.signInWithPassword({
      email: user.email,
      password: oldPassword,
    });

    if (verifyError) {
      return {
        error: {
          message: '旧密码不正确',
          code: verifyError.status?.toString(),
        },
      };
    }

    // Update password
    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (updateError) {
      if (import.meta.env.DEV) {
        console.error('Error updating password:', updateError);
      }
      return {
        error: {
          message: updateError.message || '修改密码失败',
          code: updateError.status?.toString(),
        },
      };
    }

    return { error: null };
  } catch (error: any) {
    if (import.meta.env.DEV) {
      console.error('Exception while updating password:', error);
    }
    return {
      error: {
        message: error.message || '修改密码失败，请重试',
      },
    };
  }
}

/**
 * Reset password - send reset link to email
 * Version: 1.2.9
 */
export async function resetPassword(email: string): Promise<{ error: AuthError | null }> {
  try {
    // 1.2.9: 添加详细的日志记录
    logger.log('📧 Sending password reset email to:', email);
    logger.log('📍 Redirect URL:', `${window.location.origin}/my-page`);

    const redirectTo = `${window.location.origin}/my-page`;
    
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo,
    });

    if (error) {
      // 1.2.9: 添加详细的错误日志
      logger.error('❌ Error sending password reset email:', {
        message: error.message,
        status: error.status,
        code: error.status?.toString(),
        email: email,
        redirectTo: redirectTo,
        fullError: error
      });
      
      return {
        error: {
          message: error.message || '发送密码重置链接失败',
          code: error.status?.toString(),
        },
      };
    }

    // 1.2.9: 添加成功日志
    logger.log('✅ Password reset email sent successfully:', {
      email: email,
      data: data,
      isLocal: import.meta.env.DEV,
      inbucketUrl: import.meta.env.DEV ? 'http://localhost:54324' : null
    });

    return { error: null };
  } catch (error: any) {
    // 1.2.9: 添加异常日志
    logger.error('❌ Exception while sending password reset email:', {
      error: error.message,
      stack: error.stack,
      email: email
    });
    
    return {
      error: {
        message: error.message || '发送密码重置链接失败，请重试',
      },
    };
  }
}

/**
 * Update user metadata
 * Version: 1.2.8
 */
export async function updateUserMetadata(
  data: { display_name?: string }
): Promise<{ error: AuthError | null }> {
  try {
    const { error } = await supabase.auth.updateUser({
      data,
    });

    if (error) {
      if (import.meta.env.DEV) {
        console.error('Error updating user metadata:', error);
      }
      return {
        error: {
          message: error.message || '更新用户信息失败',
          code: error.status?.toString(),
        },
      };
    }

    return { error: null };
  } catch (error: any) {
    if (import.meta.env.DEV) {
      console.error('Exception while updating user metadata:', error);
    }
    return {
      error: {
        message: error.message || '更新用户信息失败，请重试',
      },
    };
  }
}

